import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ParkingLotResponseModel } from './models/parking-lot-response-model';
import { GridDataResult } from 'node_modules/@progress/kendo-angular-grid/data/data.collection';
import { CompositeFilterDescriptor, FilterDescriptor, State } from "@progress/kendo-data-query";
import { map, tap } from 'rxjs/operators';
import { ItemResponseModelV2, ItemResponseModelV2Old } from '../items/models/item-response-model-v2';

@Injectable({
  providedIn: 'root',
})
export class ParkingLotAPIService extends BehaviorSubject<GridDataResult>  {
    public baseUrlLocal: string = environment.apiBaseUrl;
    public loading: boolean = false;

    constructor(private http: HttpClient) {
        super({ data: [], total: null });     
    }

    public query(state: State, filter: CompositeFilterDescriptor): void {
        this.loading = true;
        this.fetch(state, filter).subscribe((x) => super.next(x));
    }

    protected fetch(state: State, filter: CompositeFilterDescriptor) : Observable<GridDataResult> {
        let queryStart = '';
        let queryString = '';

        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pageindex=${state.skip}`)
        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pagesize=${state.take}`)
    
        const itemIdFilter = this.getStateFilterValue(filter, 'itemId');
        if(itemIdFilter != undefined && itemIdFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}itemId=${encodeURIComponent(itemIdFilter)}`)
        }

        const replacedByItemIdFilter = this.getStateFilterValue(filter, 'replacedByItemId');
        if(replacedByItemIdFilter != undefined && replacedByItemIdFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}replacedByItemId=${encodeURIComponent(replacedByItemIdFilter)}`)
        }
        
        const isParkedFilter = this.getStateFilterValue(filter, 'isParked');
        if(isParkedFilter != undefined && isParkedFilter.length > 0) {
            queryString = queryString.concat(`${queryStart}isParked=${encodeURIComponent(isParkedFilter)}`)
        }

        const parkedReasonCodeFilter = this.getStateFilterValue(filter, 'parkedReasonCode');
        if(parkedReasonCodeFilter != undefined && parkedReasonCodeFilter.length > 0) {
            queryString = queryString.concat(`${queryStart}parkedReasonCode=${encodeURIComponent(parkedReasonCodeFilter)}`)
        }

        return this.http.get<GridDataResult>(this.baseUrlLocal + `parkinglot/search` + queryString)
        .pipe(
            map(
            (response: GridDataResult) => {
                return {
                    data: response.data,
                    total: response.total
                };
            }
            ), tap(() => (this.loading = false))
        );
    }

    /**
     * Fetches the MessagePayload for a given eventId from the backend.
     * @param eventId The eventId of the parking lot entry
     * @returns Observable<string> with the message payload
     */
    public getParkingLotMessagePayload(itemId: string, replacedByItemId: string): Observable<ParkingLotResponseModel> {
        return this.http.get<ParkingLotResponseModel>(`${this.baseUrlLocal}parkinglot/${itemId}/${replacedByItemId}`);
    }

    public getParkingLotById(id: string): Observable<ItemResponseModelV2Old> {
        return this.http.get<ItemResponseModelV2Old>(`${this.baseUrlLocal}parkinglot/${id}`);
    }

    private getQueryStart(queryStart: string) : string {
        if(queryStart == '') {
            queryStart = '?';
        } else {
            queryStart = '&';
        }

        return queryStart;
    }

    private getStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
        if (!filter?.filters?.length) {
            return undefined;
        }

        const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
            return !('filters' in item) && item.field === field;
        });

        if (matchingFilter?.value === undefined || matchingFilter.value === null) {
            return undefined;
        }

        return String(matchingFilter.value);
    }
}


@Injectable()
export class ParkingLotService extends ParkingLotAPIService {
    constructor(http: HttpClient) {
        super(http);
    }
}